/**
 * Simple fuzzy matching with scoring.
 *
 * Scores: exact match > prefix > consecutive chars > scattered chars.
 * Works with both Latin and Cyrillic.
 */

export function fuzzyScore(query: string, text: string): number {
	const q = query.toLowerCase();
	const t = text.toLowerCase();

	if (t === q) return 1000; // exact match
	if (t.startsWith(q)) return 800 + q.length; // prefix match

	// Check if all query chars exist in order
	let qi = 0;
	let score = 0;
	let prevMatchIndex = -2; // for consecutive bonus

	for (let ti = 0; ti < t.length && qi < q.length; ti++) {
		if (t[ti] === q[qi]) {
			score += 10;
			// Consecutive char bonus
			if (ti === prevMatchIndex + 1) score += 15;
			// Word boundary bonus (char after space, -, _, . or uppercase)
			if (ti === 0 || " -_.".includes(t[ti - 1])) score += 20;
			prevMatchIndex = ti;
			qi++;
		}
	}

	// All chars matched?
	if (qi < q.length) return 0;

	return score;
}

/** Filter and sort items by fuzzy score. Returns items with score > 0. */
export function fuzzyFilter<T>(
	items: T[],
	query: string,
	getText: (item: T) => string,
): T[] {
	if (!query.trim()) return items;

	const scored = items
		.map((item) => ({ item, score: fuzzyScore(query, getText(item)) }))
		.filter((s) => s.score > 0);

	scored.sort((a, b) => b.score - a.score);
	return scored.map((s) => s.item);
}
